import { useCallback, useEffect, useMemo } from 'react';

import { ITEMS_VIEW_PARAMS, itemsViewOf } from '../utils/itemsView';
import { sortThenPatch } from '../utils/sortLevels';
import { columnsParamOf } from '../utils/savedFilterView';
import useUrlParams from './useUrlParams';

/**
 * The items page's view, read from and written to the address
 * (pages/CollectionItemsPage; moved here unchanged by KPR-1566, so the next
 * list page finds it).
 *
 * Filter, sort, and selection state live in the URL so refreshes,
 * back/forward, and shared links keep the current view. (Pagination is
 * gone — the list is an infinite feed.)
 *
 * The parameters are a CONTRACT, not this hook's private business: they are
 * `ITEMS_VIEW_PARAMS` in utils/itemsView (KPR-1579), and two surfaces outside
 * the page read the same names off the address with its `readItemsView`,
 * because they share no other channel with it:
 *
 * - components/collections/CollectionActions (the page header's ⋯): Export
 *   view… sends `card`, `q`, `review`, `sort`, `dir` and `linked` as the feed
 *   does, read with the same `review` allowlist and the same `linked`
 *   spellings; its Attach files… writes `?attach=1`, which the page reads and
 *   drops (hooks/useItemsBulkState).
 * - components/items/ItemsViewShareButton (`useItemsViewShare`, fed by
 *   CollectionActions from the address): shares `q`, `sort`, `dir`, `group`
 *   (`day` left out, as here), `review`, `linked`, `card` and the board as a
 *   filter or path short link, so a teammate lands on the same view.
 *
 * The sort is `sort` / `dir` (level 1) and, since KPR-1636, `sort2` / `dir2`
 * and `sort3` / `dir3` ("then by", read as `sortThen`, written with
 * `setSortThen`; utils/sortLevels). The header's Export view… sends them
 * (KPR-1637: the export endpoint reads them); Share this view still sends
 * level 1 alone, as short links carry one level.
 *
 * Since KPR-1641 the applied saved filter's table columns ride `cols`
 * (read as `savedColumns`, written with `setSavedColumns`; utils/savedFilterView):
 * the filter's own columns, never written to the person's table layout.
 *
 * Since KPR-1642 `saved` names the applied saved filter (read as
 * `savedFilterId`): the saved filter menu's, written when a filter is applied
 * and by a Home pin's link. It changes nothing the list asks.
 *
 * Renaming a parameter, or changing what an absent one means, changes theirs:
 * change all three together. `?view=`, `?board=`, `?lanes=` and `?attach=`
 * are read by the page itself (hooks/useViewMode and the board), not here.
 *
 * Built on hooks/useUrlParams, the one URL-state primitive, as
 * hooks/useUrlListParams (the admin lists) is.
 */

export default function useItemsViewParams() {
	// Read synchronously, on the first render too (KPR-997: a pasted
	// ?view=table link draws the table at once, its params already in hand).
	// `update` keeps one identity for the page's lifetime (useUrlParams): built
	// on react-router's `setSearchParams` it changed with every query string,
	// so selecting an item (`?item=`) restarted the feed from page 0 and paged
	// it back down: 22 requests for one click at item #181.
	//
	// `update(patch, { keepDefaults })` — `keepDefaults` writes a default value
	// out loud instead of deleting it: under a table layout's default sort,
	// choosing the platform default (primary date, newest) must still override
	// the layout, and an absent ?sort= would hand the choice straight back to it.
	const { params, has, update, searchParams } = useUrlParams(ITEMS_VIEW_PARAMS);
	const {
		cardFilter,
		query,
		review,
		sortField,
		sortDirection,
		sortThen: readSortThen,
		sortChosen,
		selectedItemId,
		groupParam,
		linkedParam,
		savedColumns: readSavedColumns,
		savedFilterId,
	} = useMemo(() => itemsViewOf(params, has), [params, has]);
	// The applied saved filter's columns (KPR-1641), [] for none: one identity
	// while they say the same, as the levels.
	const savedColumnsKey = JSON.stringify(readSavedColumns);
	const savedColumns = useMemo(() => JSON.parse(savedColumnsKey), [savedColumnsKey]);
	const setSavedColumns = useCallback(
		columns => update({ cols: columnsParamOf(columns) }),
		[update]
	);
	// The levels keep one identity while they say the same, whatever else in
	// the address moves (`?item=`), so nothing that depends on them re-runs.
	const sortThenKey = JSON.stringify(readSortThen);
	const sortThen = useMemo(() => JSON.parse(sortThenKey), [sortThenKey]);

	const setQuery = useCallback(
		text => update({ q: text ? `${text}`.trim() : undefined }),
		[update]
	);

	// "Then by" (KPR-1636): levels 2 and 3 written in one patch, so a level that
	// goes away is deleted with the others (`[]` is one level again). `extra`
	// rides the same write, e.g. a level 1 that must be written out loud.
	const setSortThen = useCallback(
		(levels, { extra = null, keepDefaults = false } = {}) => update({ ...(extra || {}), ...sortThenPatch(levels) }, { keepDefaults }),
		[update]
	);

	const setSelectedItemId = useCallback(
		id => update({ item: id || undefined }),
		[update]
	);

	// Pagination params from bookmarks of the paged era mean nothing to the
	// feed — drop them once so URLs stay canonical.
	useEffect(() => {
		if (searchParams.has('page') || searchParams.has('size')) update({ page: undefined, size: undefined });
	}, [searchParams, update]);

	return {
		cardFilter,
		query,
		review,
		sortField,
		sortDirection,
		sortThen,
		sortChosen,
		selectedItemId,
		groupParam,
		linkedParam,
		savedColumns,
		savedFilterId,
		update,
		setQuery,
		setSortThen,
		setSavedColumns,
		setSelectedItemId,
	};
}
